/**
 *
 * Returns the first non-falsy value among the provided arguments, or a fallback if all are falsy.
 * @param primary
 * @param secondary
 * @param fallback
 * @returns {*|null}
 */
export const getPreferredValue = (primary, secondary, fallback = null) => (primary || secondary || fallback);

/**
 * Attempts to parse a JSON string.
 *
 * If `value` is not valid JSON, the function returns the provided `fallback`
 * instead of throwing an error.
 *
 * @param {*} value - The value to parse (expected to be a JSON string).
 * @param {*} [fallback={}] - Value returned when parsing fails.
 * @returns {*} Parsed result or fallback.
 */
export const safeJsonParse = (value, fallback = {}) => {
  if (value == null || typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/**
 * Removes the following special characters from the input string:
 * & / \ # , + ( ) $ ~ % . ' " : * ? < > { }
 *
 * @param {string} string - The input string to sanitize.
 * @returns {string} - The string with special characters removed.
 */
export const removeSpecialCharacters = (string) => {
  return string?.replace(/[&/\\#,+()$~%.'":*?<>{}]/g, '');
};

/**
 * Adds a space in place of any `<br>` or `<br/>` HTML tags found in the input string.
 *
 * @param {string} [string=''] - The input string to process. Defaults to an empty string if not provided.
 * @returns {string} - The processed string with `<br>` tags replaced by a space.
 *
 */
export const addSpaceForBreakTag = (string = '') => {
  return string?.replace(/<\s?br\s?\/?>/g, ' ');
};

/**
 * Removes all HTML tags from the input string.
 *
 * @param {string} [string=''] - The input string to process. Defaults to an empty string if not provided.
 * @returns {string} - The processed string with all HTML tags removed.
 *
 */
export const stripHtml = (string = '') => {
  return string?.replace(/<[^>]*?>/g, '');
};

/**
 * Removes trailing newline characters from a string.
 *
 * @param {string|any} value - The value to process.
 * @returns {string|any} The string without trailing newlines, or the original value if not a string.
 */
export const removeTrailingNewline = (value) => {
  if (typeof value === 'string' && value.endsWith('\n')) {
    return value.replace(/\n+$/, '');
  }
  return value;
};

/**
 * Normalizes internal whitespace by collapsing consecutive spaces into a single space.
 *
 * @param {string} sentence \- Input text to normalize.
 * @returns {string} Text with repeated spaces reduced to one.
 */
export const removeDoubleSpaces = (sentence) => {
  return sentence?.replace(/\s{2,}/g, ' ');
};

/**
 * Checks if the provided canonical URL is valid.
 * Returns false if the URL is empty, contains 'undefined', or contains 'null'.
 *
 * @param {string} canonicalUrl - The canonical URL to validate.
 * @returns {boolean} True if valid, false otherwise.
 */
export const isCanonicalUrlValid = (canonicalUrl) => {
  if (typeof canonicalUrl !== 'string' || !canonicalUrl) return false;
  if (canonicalUrl.includes('undefined')) return false;
  if (canonicalUrl.includes('null')) return false;

  return true;
};

/**
 * Checks if the provided object is valid.
 * Returns false if the object is falsy or has no own properties.
 *
 * @param {Object} obj - The object to validate.
 * @returns {boolean} True if valid, false otherwise.
 */
export const isObjectValid = (obj) => {
  if (!obj) return false;
  if (Object.keys(obj).length === 0) return false;

  return true;
};

/**
 * Checks if the provided title is valid.
 * Returns false if the title is empty or only whitespace.
 *
 * @param {string} title - The title to validate.
 * @returns {boolean} True if valid, false otherwise.
 */
export const isTitleValid = (title) => {
  return typeof title === 'string' && Boolean(title.trim());
};

/**
 * Converts a string to title case (capitalizes the first letter of each word).
 *
 * @param {string} [string=''] - The input string to convert. Defaults to an empty string if not provided.
 * @returns {string} - The string converted to title case.
 */
export const toTitleCase = (string = '') => {
  if (!string) return '';
  return string
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
